import React, { useEffect, useState } from "react";
import { Comment } from "pages/MaterialSpecs/components/InfoPanel/Annotations/Comment/Comment";
import { useAnnotationsHandler } from "pages/MaterialSpecs/hooks/useAnnotationsHandler";
import { Box, CircularProgress, Divider, FormControlLabel, Switch, Tooltip, Typography } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import {
    getAnnotationsPermissions,
    getAnnotationsStatus,
    getShowAdditionalActions,
} from "store/features/annotations/annotations.selectors";
import { useDispatch, useSelector } from "react-redux";
import { getMaterialDetailsById, getMaterialSpecById } from "store/features/materialSpecs/specs";
import { useMaterialSpecs } from "pages/MaterialSpecs/hooks";
import { usePermissions } from "hooks";
import { getUserId } from "store/features/authorization";
import { useApproveAdMaterial } from "hooks/api/useApproveAdMaterial";
import { AnnotationAction } from "store/features/annotations/interfaces";
import BasicHeader from "pages/MaterialSpecs/components/BasicHeader/BasicHeader";
import EmptyListPlaceholder from "components/EmptyListPlaceholder/EmptyListPlaceholder";
import { fetchAnnotations } from "store/features/annotations/annotations.actions";
import { ApproveButton, ButtonContainer, PermissionWrapper, SecondaryButton } from "components";
import { useSubmitAsset } from "hooks/api/useSubmitAsset";

export const Annotations = () => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const {
        annotations,
        selectedAnnotation,
        showReviewed,
        validationError,
        onAnnotationSelect,
        onRemoveAnnotation,
        onUpdateAnnotation,
        onSaveAnnotations,
        onSetShowReviewed,
        onIsReviewedUpdate,
        onAttachAsset,
        onRemoveAsset,
    } = useAnnotationsHandler();
    const { isProcessing } = useSelector(getAnnotationsStatus);
    const { selectedMaterialSpecId, selectedMaterialId, assetId, goBack, isVisualVisible } = useMaterialSpecs();
    const materialSpec = useSelector(getMaterialSpecById(selectedMaterialSpecId));
    const materialDetails = useSelector(getMaterialDetailsById(selectedMaterialSpecId, selectedMaterialId));
    const permissions = useSelector(getAnnotationsPermissions);
    const { SAVEOTHER, SAVEOWN, SUBMITONLY, APPROVEWITHCHANGES, SUBMIT, APPROVE, SENDTOPRODUCTION } = usePermissions([
        ...permissions,
        ...(materialSpec?.permissions || []),
    ]);
    const userId = useSelector(getUserId);
    const showAdditionalActions = useSelector(getShowAdditionalActions);
    const [approveAdMaterial, { isLoading: isApprovalProcessing }] = useApproveAdMaterial();
    const [actionClicked, setActionClicked] = useState<AnnotationAction>();
    const [handleSubmitAsset, { isLoading: isVisualSubmitting }] = useSubmitAsset();

    const handleShowReviewed = (event: React.ChangeEvent<HTMLInputElement>) => {
        onSetShowReviewed(event.target.checked);
    };

    useEffect(() => {
        if (selectedMaterialSpecId && selectedMaterialId) {
            dispatch(
                fetchAnnotations.request({
                    matSpecId: selectedMaterialSpecId,
                    adMaterialId: selectedMaterialId,
                    assetId,
                })
            );
        }
    }, [dispatch, selectedMaterialId, selectedMaterialSpecId, assetId]);

    const handleVisualsSave = async (isComplete = false) => {
        if (selectedMaterialSpecId) {
            await handleSubmitAsset({
                materialSpecId: selectedMaterialSpecId,
                complete: isComplete,
            });
            onSaveAnnotations("SUBMITONLY");
            goBack();
        }
    };

    return isProcessing && annotations.length === 0 ? (
        <Box position="absolute" top="50%" left="45%">
            <CircularProgress color="primary" size={60} />
        </Box>
    ) : (
        <>
            {materialDetails && (
                <Box width="100%" pt={2} position="sticky" top="0" bgcolor="white" zIndex={1}>
                    <BasicHeader
                        bookedDimensions={materialDetails.bookedSize}
                        modificationDate={materialDetails.modificationDate}
                        dimensions={materialDetails.dimensions}
                    />
                    <FormControlLabel
                        control={<Switch color="primary" checked={showReviewed} onChange={handleShowReviewed} />}
                        label={
                            <Typography variant="subtitle2" color="textSecondary">
                                <Box fontWeight="fontWeightBold">{t("campaign.annotations.label.showReviewed")}</Box>
                            </Typography>
                        }
                    />

                    {validationError && (
                        <Typography color="error">{t("campaign.annotations.label.validationError")}</Typography>
                    )}
                    <Divider />
                </Box>
            )}
            {annotations.length > 0 ? (
                <Box>
                    <Box padding={0.5}>
                        {annotations
                            .slice(0)
                            .filter((annotation) => showReviewed || !annotation.reviewed)
                            .reverse()
                            .map((annotation) => (
                                <Comment
                                    key={annotation.selectionId}
                                    annotation={annotation}
                                    selected={
                                        selectedAnnotation && annotation.selectionId === selectedAnnotation.selectionId
                                    }
                                    onContainerSelect={onAnnotationSelect}
                                    onRemoveAnnotation={onRemoveAnnotation}
                                    onRemoveAsset={onRemoveAsset}
                                    onUpdate={onUpdateAnnotation}
                                    onAttachAsset={onAttachAsset}
                                    onIsReviewedUpdate={onIsReviewedUpdate}
                                    canEdit={
                                        isVisualVisible
                                            ? !SENDTOPRODUCTION?.disabled || false
                                            : !!(
                                                  SAVEOTHER?.display ||
                                                  (SAVEOWN?.display && annotation.creatorId === userId)
                                              )
                                    }
                                />
                            ))}
                    </Box>
                    {!isVisualVisible && showAdditionalActions && (
                        <Box width="100%" mt={1} pb={1} position="sticky" bottom={0} right={0} bgcolor="white">
                            <Box pb={1}>
                                <Divider />
                            </Box>
                            <Box display="flex" justifyContent="flex-end">
                                {SUBMITONLY?.display && (
                                    <Box mr={1}>
                                        <SecondaryButton
                                            variant="outlined"
                                            color="primary"
                                            onClick={() => {
                                                setActionClicked("SUBMITONLY");
                                                onSaveAnnotations("SUBMITONLY");
                                            }}
                                            disableRipple
                                            disabled={isProcessing || SUBMITONLY.disabled}>
                                            {isProcessing && actionClicked === "SUBMITONLY" ? (
                                                <CircularProgress color="secondary" size={20} />
                                            ) : (
                                                t("buttons.label.save")
                                            )}
                                        </SecondaryButton>
                                    </Box>
                                )}
                                {SUBMIT?.display && (
                                    <Box mr={1}>
                                        <SecondaryButton
                                            variant="outlined"
                                            color="primary"
                                            onClick={() => {
                                                setActionClicked("SUBMIT");
                                                onSaveAnnotations("SUBMIT");
                                            }}
                                            disableRipple
                                            disabled={isProcessing || SUBMIT.disabled}>
                                            {isProcessing && actionClicked === "SUBMIT" ? (
                                                <CircularProgress color="secondary" size={20} />
                                            ) : (
                                                t("buttons.label.submit")
                                            )}
                                        </SecondaryButton>
                                    </Box>
                                )}
                                {APPROVEWITHCHANGES?.display && (
                                    <ApproveButton
                                        color="primary"
                                        variant="outlined"
                                        onClick={() => {
                                            setActionClicked("APPROVEWITHCHANGES");
                                            onSaveAnnotations("APPROVEWITHCHANGES");
                                        }}
                                        disableRipple
                                        disabled={isProcessing || APPROVEWITHCHANGES.disabled}>
                                        {isProcessing && actionClicked === "APPROVEWITHCHANGES" ? (
                                            <CircularProgress color="secondary" size={20} />
                                        ) : (
                                            t("buttons.label.approveWithChanges")
                                        )}
                                    </ApproveButton>
                                )}
                            </Box>
                        </Box>
                    )}

                    {isVisualVisible && SENDTOPRODUCTION?.display && (
                        <Box width="100%" mt={1} pb={1} position="sticky" bottom={0} right={0} bgcolor="white">
                            <Box pb={1}>
                                <Divider />
                            </Box>
                            <ButtonContainer pt={2} display="flex" justifyContent="flex-end">
                                <PermissionWrapper name="SENDTOPRODUCTION" permissions={materialSpec?.permissions}>
                                    {({ disabled, tooltip }) => (
                                        <>
                                            <Tooltip title={tooltip} interactive>
                                                <span>
                                                    <SecondaryButton
                                                        disableRipple
                                                        variant="outlined"
                                                        color="primary"
                                                        disabled={disabled || isVisualSubmitting}
                                                        onClick={() => handleVisualsSave(false)}>
                                                        {t("buttons.label.submit")}
                                                    </SecondaryButton>
                                                </span>
                                            </Tooltip>
                                            <Tooltip title={tooltip} interactive>
                                                <span>
                                                    <ApproveButton
                                                        disableRipple
                                                        variant="outlined"
                                                        color="primary"
                                                        disabled={disabled || isVisualSubmitting}
                                                        onClick={() => handleVisualsSave(true)}>
                                                        {t("buttons.label.sendToProduction")}
                                                    </ApproveButton>
                                                </span>
                                            </Tooltip>
                                        </>
                                    )}
                                </PermissionWrapper>
                            </ButtonContainer>
                        </Box>
                    )}
                </Box>
            ) : (
                <EmptyListPlaceholder label={t("campaign.annotations.label.noAnnotations")} />
            )}
            {!isVisualVisible && !showAdditionalActions && APPROVE?.display && (
                <Box width="100%" mt={1} pb={1} position="sticky" bottom={0} right={0} bgcolor="white">
                    <Box pb={1}>
                        <Divider />
                    </Box>
                    <Box display="flex" justifyContent="flex-end">
                        <ApproveButton
                            color="primary"
                            variant="outlined"
                            onClick={() => {
                                if (selectedMaterialId) {
                                    approveAdMaterial(selectedMaterialId);
                                    goBack();
                                }
                            }}
                            disableRipple
                            disabled={isApprovalProcessing}>
                            {isApprovalProcessing ? (
                                <CircularProgress color="secondary" size={20} />
                            ) : (
                                t("buttons.label.approve")
                            )}
                        </ApproveButton>
                    </Box>
                </Box>
            )}
        </>
    );
};
